import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  ClinicalDocListSchema,
  type ClinicalDocKind,
  type ClinicalDocList,
} from '@datazo/contracts';
import { apiFetch } from '@/shared/api/client';

export interface ClinicalDocsFilters {
  q?: string;
  tipo?: string;
  desde?: string;
  hasta?: string;
  page: number;
  pageSize: number;
}

const ENDPOINT: Record<ClinicalDocKind, string> = {
  consent: '/api/clinical-docs/consents',
  anesthesia: '/api/clinical-docs/anesthesia',
  expense: '/api/clinical-docs/expenses',
};

export const clinicalDocsKeys = {
  list: (kind: ClinicalDocKind, filters: ClinicalDocsFilters) =>
    ['clinical-docs', kind, filters] as const,
};

function buildQs(filters: ClinicalDocsFilters): string {
  const usp = new URLSearchParams();
  if (filters.q !== undefined && filters.q.trim() !== '') usp.set('q', filters.q.trim());
  if (filters.tipo !== undefined && filters.tipo.trim() !== '') usp.set('tipo', filters.tipo.trim());
  if (filters.desde !== undefined && filters.desde !== '') usp.set('desde', filters.desde);
  if (filters.hasta !== undefined && filters.hasta !== '') usp.set('hasta', filters.hasta);
  usp.set('page', String(filters.page));
  usp.set('pageSize', String(filters.pageSize));
  return usp.toString();
}

export function useClinicalDocs(
  kind: ClinicalDocKind,
  filters: ClinicalDocsFilters,
): UseQueryResult<ClinicalDocList, Error> {
  return useQuery({
    queryKey: clinicalDocsKeys.list(kind, filters),
    queryFn: () =>
      apiFetch<ClinicalDocList>(`${ENDPOINT[kind]}?${buildQs(filters)}`, {
        parseAs: ClinicalDocListSchema,
      }),
    staleTime: 20_000,
    placeholderData: keepPreviousData,
  });
}
